import { Box, Divider, styled, useTheme } from '@mui/material'
import { MenuItem, Typography, SelectField } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { setSourceFromDataTreeMode } from 'features/analysis/state/analysisSlice'
import useDatasetsLetters from 'features/builder/state/useDatasetsLetters'
import useDatasetsEnriched from 'features/builder/ui/useDatasetsEnriched'

import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { analysisSourceFilter } from '../../../AnalysisCatalog/utils'
import { isDynamicSpatialIndexDataset } from 'features/builder/state/builderSliceUtils'

export const DATA_TREE_SOURCE = 'other'

const Letter = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: theme.spacing(2),
  width: theme.spacing(2),
  flex: `0 0 ${theme.spacing(2)}`,
  borderRadius: theme.spacing(0.25),
  color: theme.palette.common.white,
  justifyContent: 'center',
  fontSize: theme.spacing(1.5),
  marginRight: theme.spacing(0.5)
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1)
}))

function SourceSelector({ source, showHelpText, onChange, spatialIndexDatasetsAvailable = true }) {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const letters = useDatasetsLetters()
  const secondSourceFromDataTree = useAppSelector((state) => state.analysis.secondSourceTable)
  const value = secondSourceFromDataTree?.id ? secondSourceFromDataTree?.id : source !== DATA_TREE_SOURCE ? source : ''
  const baseSource = useAppSelector((state) => state.analysis.sourceDatasetId)
  const connection = useAppSelector((state) => state.analysis.connection)
  const { datasetsEnriched } = useDatasetsEnriched()

  const availableDatasets = useMemo(() => {
    return datasetsEnriched
      .filter((d) => d.connectionName === connection?.name)
      .filter((d) => d.id !== baseSource)
      .filter((d) => spatialIndexDatasetsAvailable || !isDynamicSpatialIndexDataset(d))
      ?.filter((source) => analysisSourceFilter(source))
  }, [connection, datasetsEnriched, baseSource, spatialIndexDatasetsAvailable])

  const hasChoosableDatasets = !!availableDatasets.length
  const selectedDataset = availableDatasets.find((dataset) => dataset.id === source)

  const DatasetLetterElement = ({ dataset }) => (
    <Box display="flex" alignItems={'center'}>
      {dataset.color && <Letter bgcolor={dataset.color}>{letters[dataset.id]}</Letter>}
      {dataset.label || dataset.name}
    </Box>
  )

  return (
    <Box mt={2} mb={1} aria-label="Second source select">
      <SelectField
        displayEmpty
        label={<FormattedMessage id="analysis.intersect.second" />}
        helperText={showHelpText && <FormattedMessage id="analysis.intersect.secondSourceHelp" />}
        value={value}
        onChange={(e) => {
          if (e.target.value === DATA_TREE_SOURCE) {
            e.preventDefault()
            dispatch(setSourceFromDataTreeMode(true))
          } else {
            dispatch(setSourceFromDataTreeMode(false))
          }
          onChange(e.target.value)
        }}
        renderValue={(selected) => {
          if (selected && (selectedDataset || secondSourceFromDataTree))
            return <DatasetLetterElement dataset={selectedDataset || secondSourceFromDataTree} />
          return (
            <Box display="flex">
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="analysis.addColumn.secondSourcePlaceholder" />
              </Typography>
            </Box>
          )
        }}
        size="small"
      >
        {secondSourceFromDataTree && (
          <MenuItem value={secondSourceFromDataTree.id}>{secondSourceFromDataTree.name}</MenuItem>
        )}
        {hasChoosableDatasets ? (
          availableDatasets.map((dataset) => (
            <MenuItem key={dataset.id} value={dataset.id} aria-label={`Source ${dataset.label} menu item`}>
              <DatasetLetterElement dataset={dataset} />
            </MenuItem>
          ))
        ) : (
          <MenuItem>
            <Box display="flex" alignItems="center">
              <Letter bgcolor={theme.palette.grey[300]}>-</Letter>
              <FormattedMessage id="analysis.intersect.secondSourceEmpty" />
            </Box>
          </MenuItem>
        )}
        <StyledDivider />
        <MenuItem key={DATA_TREE_SOURCE} value={DATA_TREE_SOURCE} aria-label="Others source menu item">
          <Box display="flex" alignItems="center">
            <Box>
              <FormattedMessage
                id="analysis.secondSource.otherFromConnection"
                values={{ connName: <strong>{connection?.name}</strong> }}
              />
            </Box>
          </Box>
        </MenuItem>
      </SelectField>
    </Box>
  )
}

export default SourceSelector
